import { not_implemented_type } from '@shared/asserts/utils_asserts';
import { CONSTANTS_GENERATION__value_range } from '@shared/constants/CONSTANTS_GENERATION__value_range';
import { ENUM__ml_asset_family } from '@shared/dto/+model/sd/_enums/ENUM__ml_asset_family';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { DTO_GRAPH__instance } from '../../_graph/DTO_GRAPH__instance';
import { DECORATOR__ui_without_component } from '../_base/_decorators/DECORATOR__ui_without_component';
import { ENUM__field_connectable_type } from '../_base/_methods/ENUM__field_connectable_type';
import { FIELD__base } from '../_base/FIELD__base';
import { DECORATOR__class_serialization_tested } from '../_primitives/_base/DECORATOR__class_serialization_tested';
import { DECORATOR__class_serialization_tested_for_hash } from '../_primitives/_base/DECORATOR__class_serialization_tested_for_hash';
import { DECORATOR__field_not_linked } from '../_primitives/_base/DECORATOR__field_not_linked';
import { FIELD_PRIMITIVE__bool } from '../_primitives/FIELD_PRIMITIVE__bool';
import { FIELD_PRIMITIVE__float } from '../_primitives/number/FIELD_PRIMITIVE__float';

let { UNET_FREEU_SCALE } = CONSTANTS_GENERATION__value_range;

/**
 * Configuration for the FreeU hyperparameters.
 * - https://huggingface.co/docs/diffusers/main/en/using-diffusers/freeu
 * - https://github.com/ChenyangSi/FreeU
 */

@DECORATOR__class_serialization_tested
@DECORATOR__class_serialization_tested_for_hash
export class FIELD__ml_asset_unet_free_u extends FIELD__base {
  /*
   * STATIC FIELDS
   */
  static _class_name_static = ENUM__dto_class_name.GRAPH_FIELD_ML_ASSET_UNET_FREE_U;

  /*
   * FIELDS
   */

  _is_initialized!: boolean;

  @FIELD_PRIMITIVE__bool.init_as_field({ value: false }, { field_role: '🧪 FreeU Is Enabled' })
  is_enabled!: FIELD_PRIMITIVE__bool;

  /**
   * Scaling factor for stage 1 to amplify the contributions of backbone features.
   */
  @FIELD_PRIMITIVE__float.init_as_field(
    { value: UNET_FREEU_SCALE.min, value_range: UNET_FREEU_SCALE, values_allowed: UNET_FREEU_SCALE },
    { field_role: '🧪 FreeU: B1' },
  )
  b1!: FIELD_PRIMITIVE__float;

  /**
   * Scaling factor for stage 2 to amplify the contributions of backbone features.
   */
  @FIELD_PRIMITIVE__float.init_as_field(
    { value: UNET_FREEU_SCALE.min, value_range: UNET_FREEU_SCALE, values_allowed: UNET_FREEU_SCALE },
    { field_role: '🧪 FreeU: B2' },
  )
  b2!: FIELD_PRIMITIVE__float;

  /**
   * Scaling factor for stage 1 to attenuate the contributions of the skip features.
   * This is done to mitigate the "oversmoothing effect" in the enhanced denoising process.
   */
  @FIELD_PRIMITIVE__float.init_as_field(
    { value: UNET_FREEU_SCALE.min, value_range: UNET_FREEU_SCALE, values_allowed: UNET_FREEU_SCALE },
    { field_role: '🧪 FreeU: S1' },
  )
  s1!: FIELD_PRIMITIVE__float;

  /**
   * Scaling factor for stage 2 to attenuate the contributions of the skip features.
   * This is done to mitigate the "oversmoothing effect" in the enhanced denoising process.
   */
  @FIELD_PRIMITIVE__float.init_as_field(
    { value: UNET_FREEU_SCALE.min, value_range: UNET_FREEU_SCALE, values_allowed: UNET_FREEU_SCALE },
    { field_role: '🧪 FreeU: S2' },
  )
  s2!: FIELD_PRIMITIVE__float;

  /*
   * PROPERTIES
   */

  field_connectable__type() {
    return ENUM__field_connectable_type.ML_ASSET__UNET_FREE_U;
  }

  field_connectable__value(graph: DTO_GRAPH__instance): string {
    let parts = [this.s1, this.s2, this.b1, this.b2];
    parts = parts.map(item => item.find_source(graph));

    let parts_strings = parts.map(item => item.get_value());

    return parts_strings.join(' ');
  }

  get_field_ids() {
    return [this.meta.field_id, this.is_enabled.meta.field_id, this.b1.meta.field_id, this.b2.meta.field_id, this.s1.meta.field_id, this.s2.meta.field_id];
  }

  /*
   * METHODS
   */

  constructor() {
    super();

    this._is_initialized = false;
  }

  @DECORATOR__ui_without_component
  post_init() {
    super.post_init();
  }

  copy_meta_from_parent(parent: FIELD__base) {
    super.copy_meta_from_parent(parent);

    if (this._is_initialized) return;

    let [b1, b2, s1, s2] = [UNET_FREEU_SCALE.min, UNET_FREEU_SCALE.min, UNET_FREEU_SCALE.min, UNET_FREEU_SCALE.min];

    switch (this.meta.node_family) {
      case ENUM__ml_asset_family.SD_1:
        [b1, b2, s1, s2] = [1.2, 1.4, 0.9, 0.2];
        break;
      case ENUM__ml_asset_family.SD_2:
        [b1, b2, s1, s2] = [1.1, 1.2, 0.9, 0.2];
        break;
      case ENUM__ml_asset_family.SD_XL:
        [b1, b2, s1, s2] = [1.1, 1.2, 0.6, 0.4];
        break;
      default:
        not_implemented_type(this.meta.node_family);
    }

    this.b1.set_value(b1);
    this.b2.set_value(b2);

    this.s1.set_value(s1);
    this.s2.set_value(s2);

    this._is_initialized = true;
  }

  @DECORATOR__field_not_linked()
  has_effect_on_generation_runtime(graph: DTO_GRAPH__instance): boolean {
    if (!this.is_enabled.get_value()) return false;

    return super.has_effect_on_generation_runtime(graph);
  }

  @DECORATOR__field_not_linked()
  store__serialization_keys_list_white_for_hash(): string[] {
    let out: (keyof this)[] = [];

    out.push('b1');
    out.push('b2');
    out.push('s1');
    out.push('s2');

    return out as string[];
  }
}
